import { bindActionCreators } from '@reduxjs/toolkit';
import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Challenge } from 'src/model/Challenge';
import { ChallengeTypes } from 'src/model/enum/dataEnums';
import { PageSource, WordListType } from 'src/model/enum/enums';
import { WordList } from 'src/model/WordList';
import { challengeActions } from 'src/store/slices/challengeSlice';
import { RootState } from 'src/store/store';
import { GetChallengeDataUrl } from 'src/utils/URLUtils';
import useFetch from '../api/useFetch';
import { useGeneralSettings } from './useGeneralSettings';
import { useLearning } from './useLearning';
import useUser from './useUser';

export const useChallenge = () => {
    const dispatch = useDispatch();
    const { autoPlay, selectedLists, isFiltered, pageSource } = useSelector((state: RootState) => state.challenge);
    const { learningInfo } = useLearning();
    const { send } = useFetch<any>();
    const [isLoading, setIsLoading] = useState(false);
    const randomIndices = useRef<Map<number, number>>(new Map());
    const ac = bindActionCreators(challengeActions, dispatch);
    const { userInfo } = useUser();
    const { generalSettings, updateGeneralSettings } = useGeneralSettings();



    const setPageSource = useCallback((source: PageSource) => {
        ac.setPageSource(source);
    }, [ac]);

    const checkAndUpdateChallengeCount = useCallback(() => {
        const currentCount = typeof generalSettings.ChallengeCount === 'number' ? generalSettings.ChallengeCount : 0;
        const newCount = currentCount + 1;

        if (newCount % 10 === 0) {
            updateGeneralSettings({ ChallengeCount: 0 });
            return true;
        }

        updateGeneralSettings({ ChallengeCount: newCount });
        return false;
    }, [generalSettings, updateGeneralSettings]);

    const setSelectedLists = useCallback((lists: WordList[]) => {
        ac.setSelectedLists(lists);
    }, [ac]);

    const setIsFiltered = useCallback((value: boolean) => {
        ac.setIsFiltered(value);
    }, [ac]);

    const setAutoPlay = useCallback((value: boolean) => {
        ac.setAutoPlay(value);
    }, [ac]);

    const getRandomChallenge = useCallback((challenges: Challenge[], wordId: number) => {
        if (!userInfo?.AutoPronounce) {
            challenges = challenges
                .filter(c => c.Nature !== ChallengeTypes.GivenAudioPickDefinition
                    && c.Nature !== ChallengeTypes.GivenAudioPickSpelling
                );
        }
        if (userInfo?.ChallengeTypes) {
            challenges = challenges.filter(c => userInfo?.ChallengeTypes
                .includes(c.Nature));
        }

        let randomIndex = randomIndices.current.get(wordId);
        if (randomIndex === undefined || randomIndex >= challenges.length) {
            randomIndex = Math.floor(Math.random() * challenges.length);
            randomIndices.current.set(wordId, randomIndex);
        }

        return challenges[randomIndex];
        //return challenges.find(c => c.Nature === ChallengeTypes.GivenAudioPickDefinition);
    }, [userInfo?.AutoPronounce, userInfo?.ChallengeTypes]);

    const getFilteredWordIds = useCallback(() => {
        if (!learningInfo?.ReviewWordIds) return [];

        const reviewWordIds = learningInfo.ReviewWordIds.map(id => parseInt(id.split(':')[0]));


        if (!isFiltered) {
            return reviewWordIds;
        }

        // Get all valid word IDs from selected lists
        const validWordIds = selectedLists.flatMap(list => {
            if (list.Type === WordListType.All) {
                return reviewWordIds;
            }
            if (list.Type === WordListType.Custom && list.CustomListData) {
                return list.CustomListData.map(item => item.WordId);
            }
            if (list.Type === WordListType.Subject && list.WordListData) {
                return list.WordListData;
            }
            return [];
        });

        return reviewWordIds.filter(id => validWordIds.includes(id));
    }, [learningInfo?.ReviewWordIds, selectedLists, isFiltered]);


    const getChallenge = useCallback(async (wordId: number) => {
        const { response } = await send(GetChallengeDataUrl(wordId));
        if (!response) return;
        const challenge = getRandomChallenge(response.Challenges, wordId);
        if (!challenge) return;
        return { challenge, wordId };
    }, [getRandomChallenge, send]);

    const getNextChallenge = useCallback(async () => {
        if (!learningInfo) return;

        const filteredWordIds = getFilteredWordIds();

        if (filteredWordIds.length === 0) return;

        const wordId = filteredWordIds[0];

        return getChallenge(wordId);

    }, [learningInfo, getFilteredWordIds, getChallenge]);


    const getReviewCount = useCallback((wordId: number) => {
        if (!learningInfo?.ReviewWordIds) return 0;
        const challenge = learningInfo.ReviewWordIds.find(id => id.split(':')[0] === wordId.toString());
        return challenge ? parseInt(challenge.split(':')[1]) : 0;
    }, [learningInfo?.ReviewWordIds]);


    return {
        getNextChallenge,
        getChallenge,
        checkAndUpdateChallengeCount,
        isLoading,
        autoPlay,
        setAutoPlay,
        selectedLists,
        setSelectedLists,
        isFiltered,
        setIsFiltered,
        getReviewCount,
        pageSource,
        setPageSource
    };
};